import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, Image, Loader, Select, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { api } from 'src/common/utils/api';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useCompanies } from 'src/secret-menu/hooks/useCompanies';
import { useAutoBrandFromUrl } from 'src/secret-menu/smart-create/hooks/useAutoBrandFromUrl';

type BackgroundProcessStatus = 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED';

interface BackgroundProcessResponse {
  id: string;
  status: BackgroundProcessStatus;
  statusMessage: string;
  extraDetails: Record<string, string> | null;
}

const POLLING_INTERVAL_MS = 2000;
const isTerminal = (s: BackgroundProcessStatus) => s === 'SUCCESS' || s === 'FAILED';

export default function AutoBrandFromUrlTab() {
  const { companies, isLoading: companiesLoading } = useCompanies();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const { extractFromUrl, isExtracting } = useAutoBrandFromUrl(selectedCompanyId);

  const form = useForm({
    initialValues: { websiteUrl: '' },
    validate: {
      websiteUrl: v => (v.trim().length === 0 ? 'Website URL is required' : null),
    },
  });

  const { data: processStatus } = useQuery<BackgroundProcessResponse>({
    queryKey: ['admin', 'companies', selectedCompanyId, 'background-processes', taskId],
    queryFn: async () => {
      const { data } = await api.get<BackgroundProcessResponse>(
        `admin/companies/${selectedCompanyId}/background-processes/${taskId}`,
      );
      return data;
    },
    enabled: !!taskId && !!selectedCompanyId,
    refetchInterval: query =>
      query.state.data && isTerminal(query.state.data.status) ? false : POLLING_INTERVAL_MS,
  });

  const handleSubmit = form.onSubmit(async values => {
    setTaskId(null);
    const response = await extractFromUrl(values.websiteUrl);
    setTaskId(response.taskId);
  });

  const isPolling = !!taskId && (!processStatus || !isTerminal(processStatus.status));
  const isRunning = isExtracting || isPolling;
  const taskSucceeded = processStatus?.status === 'SUCCESS';

  const { data: logoData } = useQuery<{ logoUrl: string | null }>({
    queryKey: ['admin', 'companies', selectedCompanyId, 'export-branding', 'logo', taskId],
    queryFn: async () => {
      const { data } = await api.get<{ logoUrl: string | null }>(
        `admin/companies/${selectedCompanyId}/export-branding/logo`,
      );
      return data;
    },
    enabled: !!selectedCompanyId && taskSucceeded,
  });

  const companyItems = companies?.map(c => ({ value: c.id, label: c.name })) ?? [];

  return (
    <Stack gap="lg" maw={560} p="md" bg="white">
      <Stack gap={4}>
        <Text size="displayXs" fw={FONT_WEIGHTS.semibold}>
          Auto brand from URL test
        </Text>
        <Text size="textMd" c="gray.6">
          Extract and apply brand colours from a company website.
        </Text>
      </Stack>

      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <Select
            label="Company"
            placeholder="Select a company"
            data={companyItems}
            value={selectedCompanyId}
            onChange={setSelectedCompanyId}
            disabled={companiesLoading}
            searchable
          />
          <TextInput
            label="Website URL"
            placeholder="e.g. https://example.co.uk"
            {...form.getInputProps('websiteUrl')}
          />
          <Button
            type="submit"
            disabled={isRunning || !selectedCompanyId}
            leftSection={isRunning ? <Loader size="xs" /> : undefined}
            w="fit-content"
          >
            {isRunning ? 'Extracting…' : 'Extract branding'}
          </Button>
        </Stack>
      </form>

      {taskSucceeded && processStatus.extraDetails && (
        <Stack gap="xs">
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.8">
            Branding applied
          </Text>
          {logoData?.logoUrl && (
            <Image
              src={logoData.logoUrl}
              alt="Company logo"
              fit="contain"
              h={80}
              w="auto"
              maw={300}
              style={{ background: '#f8f9fa', borderRadius: 8, padding: 8 }}
            />
          )}
          <ColorSwatch
            label="Primary"
            hex={extractColor(processStatus.extraDetails, 'primary_color')}
          />
          <ColorSwatch
            label="Secondary"
            hex={extractColor(processStatus.extraDetails, 'secondary_color')}
          />
          <ColorSwatch
            label="Accent"
            hex={extractColor(processStatus.extraDetails, 'accent_color')}
          />
          <MissingFields raw={processStatus.extraDetails.missing_fields} />
        </Stack>
      )}

      {processStatus?.status === 'FAILED' && (
        <Alert color="red" title="Extraction failed">
          {processStatus.statusMessage}
        </Alert>
      )}
    </Stack>
  );
}

function ColorSwatch({ label, hex }: { label: string; hex: string | null }) {
  if (!hex) return null;
  return (
    <Badge
      leftSection={
        <div
          style={{
            width: 12,
            height: 12,
            borderRadius: 2,
            backgroundColor: `#${hex}`,
            border: '1px solid rgba(0,0,0,0.15)',
          }}
        />
      }
      variant="light"
      color="gray"
    >
      {label}: #{hex}
    </Badge>
  );
}

function MissingFields({ raw }: { raw: string | undefined }) {
  if (!raw) return null;
  const fields = raw
    .split(',')
    .map(f => f.trim())
    .filter(Boolean);
  if (fields.length === 0) return null;
  return (
    <Alert color="yellow" title="Fields not detected">
      {fields.join(', ')}
    </Alert>
  );
}

function extractColor(extraDetails: Record<string, string>, field: string): string | null {
  try {
    const config = JSON.parse(extraDetails.config ?? '{}') as Record<string, unknown>;
    const palette = config.color_palette as Record<string, string> | undefined;
    return palette?.[field] ?? null;
  } catch {
    return null;
  }
}
